// src/components/sesiones/SesionesGenerales.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Calendar, Clock, User, Banknote,
    Eye, Filter, Search, RefreshCw, FileText, FileSpreadsheet, CreditCard, ChevronRight, Home, X
} from 'lucide-react';
import {
    fetchSesionesPOS,
    SesionPOS,
    fetchReporteSesionPOS
} from '../../../services/pos/sesionPOSService';
import { fetchPuntosDeVenta, PuntoDeVenta } from '../../../services/pos/puntoDeVentaService';
import { generarPDFReporteSesionPOS } from '../../../utils/pdfReporteSesionPOS';
import { generarExcelReporteSesionPOS } from '../../../utils/excelReporteSesionPOS';

// ── Paleta de diseño Co Marca ─────────────────────────────
const colors = {
    primary:        '#FF2052',
    primaryDark:    '#9B004A',
    secondary:      '#007FC8',
    secondaryLight: '#00DBB9',
    accent:         '#FFD97A',
    accentDark:     '#EC8400',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.ses-gen * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes sesFadeUp { from{opacity:0;transform:translateY(16px);} to{opacity:1;transform:translateY(0);} }
@keyframes spin { to{transform:rotate(360deg);} }
.ses-fadeup   { animation:sesFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ses-fadeup-1 { animation:sesFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.ses-table-row { transition:background .12s; }
.ses-table-row:hover { background:#fafcff; }

.ses-btn-view {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'DM Sans',sans-serif;
    background:${colors.secondary}12; color:${colors.secondary};
    transition:all .2s;
}
.ses-btn-view:hover { background:${colors.secondary}; color:#fff; transform:translateY(-1px); }

.ses-btn-pdf {
    display:inline-flex; align-items:center; gap:5px;
    padding:6px 12px; border-radius:8px; border:none; cursor:pointer;
    font-size:.78rem; font-weight:700; font-family:'DM Sans',sans-serif;
    background:${colors.secondaryLight}12; color:#065f46;
    transition:all .2s;
}
.ses-btn-pdf:hover:not(:disabled) { background:${colors.secondaryLight}; color:#fff; transform:translateY(-1px); }
.ses-btn-pdf:disabled { opacity:.5; cursor:not-allowed; }

.ses-btn-back {
    display:inline-flex; align-items:center; gap:7px;
    background:#fff; border:1.5px solid #e2e8f0;
    color:#374151; border-radius:11px;
    padding:9px 16px; font-size:.85rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.ses-btn-back:hover { background:#f8fafc; border-color:${colors.secondary}40; color:${colors.secondary}; }

.ses-btn-filter {
    display:inline-flex; align-items:center; gap:6px;
    padding:9px 16px; border-radius:11px; border:1.5px solid #e2e8f0;
    background:#fff; color:#374151; font-size:.85rem; font-weight:700;
    cursor:pointer; transition:all .2s; font-family:'DM Sans',sans-serif;
}
.ses-btn-filter:hover { background:#f8fafc; border-color:${colors.secondary}40; color:${colors.secondary}; }

.ses-btn-clear {
    display:inline-flex; align-items:center; gap:6px;
    padding:9px 16px; border-radius:11px; border:1.5px solid #e2e8f0;
    background:#f8fafc; color:#374151; font-size:.85rem; font-weight:700;
    cursor:pointer; transition:all .2s; font-family:'DM Sans',sans-serif;
}
.ses-btn-clear:hover { background:#f1f5f9; }

.ses-input {
    width:100%; padding:10px 14px; border-radius:10px; border:1.5px solid #e5e7eb;
    background:#fafafa; font-size:.88rem; color:#374151; outline:none;
    transition:all .2s; font-family:'DM Sans',sans-serif;
}
.ses-input:focus { border-color:${colors.secondary}; box-shadow:0 0 0 3px ${colors.secondary}12; background:#fff; }

.ses-select {
    width:100%; padding:10px 14px; border-radius:10px; border:1.5px solid #e5e7eb;
    background:#fafafa; font-size:.88rem; color:#374151; outline:none;
    transition:all .2s; font-family:'DM Sans',sans-serif;
    appearance:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat:no-repeat; background-position:right 14px center;
}
.ses-select:focus { border-color:${colors.secondary}; box-shadow:0 0 0 3px ${colors.secondary}12; background:#fff; }

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(0,127,200,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(0,127,200,.5); }
`;

interface FiltrosSesiones {
    punto_de_venta: string;
    usuario: string;
    estado: string;
    fecha_inicio: string;
    fecha_fin: string;
    buscar: string;
}

const SesionesPOSGeneral: React.FC = () => {
    const navigate = useNavigate();
    const [sesiones, setSesiones] = useState<SesionPOS[]>([]);
    const [puntosDeVenta, setPuntosDeVenta] = useState<PuntoDeVenta[]>([]);
    const [loading, setLoading] = useState(true);
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
    const [generandoPDF, setGenerandoPDF] = useState<number | null>(null);
    const [generandoExcel, setGenerandoExcel] = useState<number | null>(null);
    const [filtros, setFiltros] = useState<FiltrosSesiones>({
        punto_de_venta: '',
        usuario: '',
        estado: '',
        fecha_inicio: '',
        fecha_fin: '',
        buscar: ''
    });

    // ── Helpers ──────────────────────────────────────────────
    const formatSaldo = (v: any) => {
        const n = Number(v);
        return isNaN(n) ? '0.00' : n.toFixed(2);
    };
    const formatDate = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleDateString('es-ES', { year:'numeric', month:'short', day:'numeric' });
    };
    const formatTime = (d: string) => {
        if (!d) return 'N/A';
        return new Date(d).toLocaleTimeString('es-ES', { hour:'2-digit', minute:'2-digit' });
    };
    const getEstadoStyle = (estado: string) => {
        switch (estado) {
            case 'cerrada': return { bg:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', dot:colors.secondaryLight };
            case 'abierta': return { bg:`${colors.secondary}12`, border:`1px solid ${colors.secondary}25`, color:colors.secondary, dot:colors.secondary };
            default:        return { bg:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, dot:colors.primary };
        }
    };

    const handleGenerarPDF = async (sesionId: number) => {
        setGenerandoPDF(sesionId);
        try {
            const data = await fetchReporteSesionPOS(sesionId);
            await generarPDFReporteSesionPOS(data);
        } catch { alert('Error al generar el reporte.'); }
        finally { setGenerandoPDF(null); }
    };

    const handleGenerarExcel = async (sesionId: number) => {
        setGenerandoExcel(sesionId);
        try {
            const data = await fetchReporteSesionPOS(sesionId);
            await generarExcelReporteSesionPOS(data);
        } catch { alert('Error al generar el reporte Excel.'); }
        finally { setGenerandoExcel(null); }
    };

    useEffect(() => {
        const cargarDatos = async () => {
            try {
                setLoading(true);
                const [sesionesData, puntosData] = await Promise.all([
                    fetchSesionesPOS(),
                    fetchPuntosDeVenta()
                ]);
                setSesiones(sesionesData);
                setPuntosDeVenta(puntosData);
            } catch (error) {
                /* Browser diagnostics omitted. */
            } finally { setLoading(false); }
        };
        cargarDatos();
    }, []);

    const aplicarFiltros = () => {
        setLoading(true);
        setTimeout(async () => {
            try {
                let sesionesFiltradas = await fetchSesionesPOS();
                if (filtros.punto_de_venta) {
                    sesionesFiltradas = sesionesFiltradas.filter(s => s.punto_de_venta.toString() === filtros.punto_de_venta);
                }
                if (filtros.estado) {
                    sesionesFiltradas = sesionesFiltradas.filter(s => s.estado === filtros.estado);
                }
                if (filtros.fecha_inicio) {
                    const fi = new Date(filtros.fecha_inicio);
                    sesionesFiltradas = sesionesFiltradas.filter(s => new Date(s.fecha_apertura) >= fi);
                }
                if (filtros.fecha_fin) {
                    const ff = new Date(filtros.fecha_fin);
                    ff.setHours(23,59,59);
                    sesionesFiltradas = sesionesFiltradas.filter(s => new Date(s.fecha_apertura) <= ff);
                }
                if (filtros.buscar) {
                    const t = filtros.buscar.toLowerCase();
                    sesionesFiltradas = sesionesFiltradas.filter(s =>
                        s.usuario_nombre?.toLowerCase().includes(t) ||
                        s.punto_de_venta_nombre?.toLowerCase().includes(t) ||
                        s.observaciones_apertura.toLowerCase().includes(t) ||
                        s.observaciones_cierre.toLowerCase().includes(t)
                    );
                }
                setSesiones(sesionesFiltradas);
            } catch (error) {
                /* Browser diagnostics omitted. */
            } finally { setLoading(false); }
        }, 300);
    };

    const limpiarFiltros = () => {
        setFiltros({ punto_de_venta:'', usuario:'', estado:'', fecha_inicio:'', fecha_fin:'', buscar:'' });
        setLoading(true);
        fetchSesionesPOS().then(data => { setSesiones(data); setLoading(false); }).catch(() => setLoading(false));
    };

    const handleFiltroChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    if (loading && sesiones.length === 0) return (
        <>
            <style>{css}</style>
            <div className="ses-gen" style={{ background:'#fff', minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ textAlign:'center' }}>
                    <div style={{ width:'48px', height:'48px', position:'relative', margin:'0 auto 14px' }}>
                        <div style={{ position:'absolute', inset:0, border:`3px solid ${colors.secondary}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
                        <div style={{ position:'absolute', inset:'8px', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <Calendar size={14} color="#fff"/>
                        </div>
                    </div>
                    <p style={{ color:'#64748b', fontWeight:'600' }}>Cargando sesiones...</p>
                </div>
            </div>
        </>
    );

    return (
        <>
            <style>{css}</style>
            <div className="ses-gen" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="ses-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/pos-list" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Puntos de Venta
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Sesiones Generales</span>
                    </nav>

                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.4rem,3vw,1.9rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Sesiones <span style={{ color:colors.primary }}>Generales</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>
                                {sesiones.length} sesiones registradas en el sistema
                            </p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:'10px' }}>
                            <button className="ses-btn-filter" onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}>
                                <Filter size={14}/> Filtros
                            </button>
                            <button className="ses-btn-clear" onClick={limpiarFiltros}>
                                <RefreshCw size={14}/> Limpiar
                            </button>
                            <button className="ses-btn-back" onClick={() => navigate('/pos-list')}>
                                <ArrowLeft size={14}/> Volver
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── FILTROS ── */}
                {filtrosAbiertos && (
                    <div className="ses-fadeup-1" style={{
                        background:'#fff', borderRadius:'16px', border:'1.5px solid #f1f5f9',
                        padding:'20px', marginBottom:'20px', display:'grid',
                        gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:'16px'
                    }}>
                        <div>
                            <label style={{ fontSize:'.68rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px', display:'block' }}>POS</label>
                            <select name="punto_de_venta" value={filtros.punto_de_venta} onChange={handleFiltroChange} className="ses-select">
                                <option value="">Todos</option>
                                {puntosDeVenta.map(pos => <option key={pos.id} value={pos.id}>{pos.nombre}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize:'.68rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px', display:'block' }}>Estado</label>
                            <select name="estado" value={filtros.estado} onChange={handleFiltroChange} className="ses-select">
                                <option value="">Todos</option>
                                <option value="abierta">Abierta</option>
                                <option value="cerrada">Cerrada</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize:'.68rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px', display:'block' }}>Desde</label>
                            <input type="date" name="fecha_inicio" value={filtros.fecha_inicio} onChange={handleFiltroChange} className="ses-input" />
                        </div>
                        <div>
                            <label style={{ fontSize:'.68rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px', display:'block' }}>Hasta</label>
                            <input type="date" name="fecha_fin" value={filtros.fecha_fin} onChange={handleFiltroChange} className="ses-input" />
                        </div>
                        <div>
                            <label style={{ fontSize:'.68rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px', display:'block' }}>Buscar</label>
                            <div style={{ position:'relative' }}>
                                <Search size={13} color="#94a3b8" style={{ position:'absolute', left:'10px', top:'50%', transform:'translateY(-50%)' }}/>
                                <input type="text" name="buscar" value={filtros.buscar} onChange={handleFiltroChange} placeholder="Usuario, POS..." className="ses-input" style={{ paddingLeft:'32px' }}/>
                            </div>
                        </div>
                        <div style={{ display:'flex', alignItems:'flex-end', gap:'8px' }}>
                            <button onClick={aplicarFiltros} style={{
                                display:'inline-flex', alignItems:'center', gap:'6px', padding:'9px 16px', borderRadius:'11px',
                                border:'none', background:`linear-gradient(135deg,${colors.secondary},#004d7a)`, color:'#fff',
                                fontSize:'.85rem', fontWeight:'700', cursor:'pointer', transition:'all .2s'
                            }}>
                                <Search size={14}/> Aplicar
                            </button>
                            <button onClick={() => setFiltrosAbiertos(false)} style={{
                                padding:'9px 14px', borderRadius:'11px', border:'1.5px solid #e2e8f0', background:'#fff',
                                color:'#64748b', cursor:'pointer'
                            }}><X size={14}/></button>
                        </div>
                    </div>
                )}

                {/* ── TABLA DE SESIONES ── */}
                <section aria-label="Sesiones del POS" className="ses-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #eef2f7', boxShadow:'0 10px 36px rgba(15,23,42,.055)', overflow:'hidden' }}>
                    <div style={{ minHeight:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})` }}/>
                    <div style={{ padding:'14px 20px', borderBottom:'1px solid #eef2f7', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'9px' }}>
                            <div style={{ width:'34px', height:'34px', borderRadius:'11px', background:`${colors.secondary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}><Calendar size={16} color={colors.secondary}/></div>
                            <div><strong style={{ display:'block', fontSize:'.86rem', color:'#0f172a' }}>{sesiones.length} sesiones</strong><span style={{ fontSize:'.72rem', color:'#94a3b8' }}>Historial operativo y financiero</span></div>
                        </div>
                        <span style={{ fontSize:'.7rem', color:'#94a3b8' }}>Desliza horizontalmente para ver todo</span>
                    </div>
                    <div className="custom-scroll" style={{ overflowX:'auto' }}>
                        <table aria-label="Sesiones del POS" style={{ width:'100%', minWidth:'1120px', borderCollapse:'collapse' }}>
                            <thead>
                                <tr style={{ background:'#f8fafc', borderBottom:'1px solid #e8edf3' }}>
                                    {[['Sesión', null], ['Horario', <Clock size={11}/>], ['Caja', <Banknote size={11}/>], ['Actividad', <CreditCard size={11}/>], ['Estado', null], ['Acciones', null]].map(([label,icon]) => (
                                        <th key={String(label)} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.65rem', fontWeight:800, color:'#64748b', textTransform:'uppercase', letterSpacing:'.075em', whiteSpace:'nowrap' }}><span style={{ display:'inline-flex', alignItems:'center', gap:'5px' }}>{icon}{label}</span></th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sesiones.map(ses => {
                                    const est = getEstadoStyle(ses.estado);
                                    const categories = ses.totales_por_categoria || { efectivo:'0.00', banco:'0.00', otro:'0.00' };
                                    return (
                                        <tr key={ses.id} className="ses-table-row" style={{ borderBottom:'1px solid #eef2f7', verticalAlign:'top' }}>
                                            <td style={{ padding:'16px 18px' }}>
                                                <strong style={{ display:'block', fontSize:'.8rem', color:colors.primary, letterSpacing:'.02em' }}>{ses.codigo || `Sesión #${ses.id}`}</strong>
                                                <span style={{ display:'block', marginTop:'4px', fontSize:'.88rem', color:'#0f172a', fontWeight:800 }}>{ses.punto_de_venta_nombre || 'Punto de venta'}</span>
                                                <span style={{ display:'flex', alignItems:'center', gap:'5px', marginTop:'4px', fontSize:'.72rem', color:'#64748b' }}><User size={12}/>{ses.usuario_nombre || 'Sin usuario'}</span>
                                            </td>
                                            <td style={{ padding:'16px 18px', whiteSpace:'nowrap' }}>
                                                <span style={{ display:'block', fontSize:'.78rem', color:'#334155', fontWeight:700 }}>Abrió {formatDate(ses.fecha_apertura)}</span>
                                                <span style={{ display:'block', marginTop:'3px', fontSize:'.7rem', color:'#94a3b8' }}>{formatTime(ses.fecha_apertura)}</span>
                                                <span style={{ display:'block', marginTop:'8px', fontSize:'.72rem', color:ses.fecha_cierre ? '#64748b' : colors.secondary, fontWeight:650 }}>{ses.fecha_cierre ? `Cerró ${formatDate(ses.fecha_cierre)} · ${formatTime(ses.fecha_cierre)}` : 'Sesión en curso'}</span>
                                            </td>
                                            <td style={{ padding:'16px 18px', whiteSpace:'nowrap' }}>
                                                <span style={{ display:'block', fontSize:'.68rem', color:'#94a3b8', fontWeight:700 }}>INICIAL</span><strong style={{ fontSize:'.85rem', color:'#0f172a' }}>{formatSaldo(ses.saldo_inicial)} Bs.</strong>
                                                <span style={{ display:'block', marginTop:'8px', fontSize:'.68rem', color:'#94a3b8', fontWeight:700 }}>FINAL</span><strong style={{ fontSize:'.85rem', color:'#0f172a' }}>{ses.saldo_final !== null ? `${formatSaldo(ses.saldo_final)} Bs.` : 'Pendiente'}</strong>
                                            </td>
                                            <td style={{ padding:'16px 18px', minWidth:'315px' }}>
                                                <div style={{ display:'flex', gap:'10px', marginBottom:'9px', color:'#475569', fontSize:'.72rem', fontWeight:700 }}><span>{ses.reservas_count || 0} reservas</span><span>•</span><span>{ses.pagos_count || 0} pagos</span><span>•</span><strong style={{ color:'#0f172a' }}>{formatSaldo(ses.total_pagos)} Bs.</strong></div>
                                                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                                                    {([['efectivo','Efectivo',colors.secondaryLight],['banco','Banco',colors.secondary],['otro','Otros',colors.accentDark]] as const).map(([key,label,color]) => {
                                                        const category = ses.categorias_pago?.find((item: { categoria: string; cantidad: number; monto: string }) => item.categoria === key);
                                                        const count = category?.cantidad ?? ses.tipos_pago?.filter((tipo: { categoria: string }) => tipo.categoria === key).reduce((total: number, tipo: { cantidad: number }) => total + tipo.cantidad, 0) ?? 0;
                                                        return <span key={key} style={{ padding:'5px 8px', borderRadius:'8px', background:`${color}0d`, border:`1px solid ${color}1f`, fontSize:'.66rem', color:'#475569', whiteSpace:'nowrap' }}><strong>{label} · {count}</strong> &nbsp;{formatSaldo(category?.monto ?? categories[key])} Bs.</span>;
                                                    })}
                                                </div>
                                                {Boolean(ses.tipos_pago?.length) && <div style={{ marginTop:'7px', fontSize:'.65rem', color:'#64748b' }}>{ses.tipos_pago!.map((tipo: { nombre: string; cantidad: number; monto: string }) => `${tipo.nombre}: ${tipo.cantidad} · ${formatSaldo(tipo.monto)} Bs.`).join('  •  ')}</div>}
                                            </td>
                                            <td style={{ padding:'16px 18px', whiteSpace:'nowrap' }}><span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:est.bg, border:est.border, color:est.color, borderRadius:'100px', padding:'4px 10px', fontSize:'.7rem', fontWeight:800, textTransform:'capitalize' }}><span style={{ width:'5px', height:'5px', borderRadius:'50%', background:est.dot }}/>{ses.estado}</span></td>
                                            <td style={{ padding:'16px 18px', whiteSpace:'nowrap' }}><div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}><button className="ses-btn-view" onClick={()=>navigate(`/sesiones-pos/${ses.id}`)}><Eye size={13}/> Detalles</button><button className="ses-btn-pdf" disabled={generandoPDF===ses.id} onClick={()=>handleGenerarPDF(ses.id)}><FileText size={13}/> PDF</button><button className="ses-btn-pdf" disabled={generandoExcel===ses.id} onClick={()=>handleGenerarExcel(ses.id)}><FileSpreadsheet size={13}/> Excel</button></div></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
                {sesiones.length===0 && !loading && (<div style={{ padding:'60px 24px', textAlign:'center', border:'1.5px solid #f1f5f9', borderRadius:'20px' }}><Calendar size={26} color={colors.secondary}/><p style={{ fontWeight:800, color:'#0f172a' }}>No se encontraron sesiones</p><p style={{ color:'#94a3b8', fontSize:'.85rem' }}>Intenta ajustar los filtros.</p></div>)}

                {/* ── INFO REPORTES ── */}
                {sesiones.length > 0 && (
                    <div style={{ marginTop:'16px', display:'flex', gap:'10px', background:`${colors.secondary}08`, border:`1.5px solid ${colors.secondary}20`, borderRadius:'14px', padding:'14px 18px' }}>
                        <FileText size={16} color={colors.secondary} style={{ flexShrink:0, marginTop:'2px' }}/>
                        <div>
                            <p style={{ fontSize:'.82rem', color:'#374151', fontWeight:'600', marginBottom:'2px' }}>Reportes disponibles</p>
                            <p style={{ fontSize:'.78rem', color:'#64748b', lineHeight:1.5 }}>
                                Genera reportes PDF y Excel individuales para cada sesión. Cada reporte incluye reservas, tipos de pago, montos y movimientos de caja.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SesionesPOSGeneral;

